import { inject, Injectable } from '@angular/core';
import {
    defaultDatiqMapStyleConfiguration,
    emptyDatIqMapTooltip,
    IDatiqMapGeoJson,
    IDatiqMapGeoJsonFeature,
    IDatiqMapTooltip,
    IIqMcMapGeoJsonFeaturePropertiesStyles,
    MciRatio,
} from '../../models';
import { IqMcMapUtilsService } from './iq-mc-map-utils.service';
import { isNil } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class MciMapService {
    constructor() {}

    private _infoWindow: google.maps.InfoWindow;
    private _utils = inject(IqMcMapUtilsService);

    computeFeatures(
        mciData: MciRatio[],
        featureCollection: IDatiqMapGeoJson,
    ): IDatiqMapGeoJson {
        const computedFeatures: IDatiqMapGeoJsonFeature[] =
            featureCollection.features.map(
                (feature: IDatiqMapGeoJsonFeature): IDatiqMapGeoJsonFeature => {
                    const coloredFeature: IDatiqMapGeoJsonFeature =
                        this._mapMciDataToFeature(mciData, feature);
                    return coloredFeature;
                },
            );
        return { ...featureCollection, features: computedFeatures };
    }

    setMapFeatures(map: google.maps.Map, styledGeographies: IDatiqMapGeoJson) {
        this._utils.setGeoJsonToMap(map, styledGeographies);
        this._utils.setMapStyles(map);
        this._utils.addOverlayMap(map);
        this._setInfoWindow(map);
    }

    // #region InfoWindow Methods

    private _setInfoWindow(map: google.maps.Map) {
        const infoWindow = this._getInfoWindow();
        map?.data.addListener(
            'mouseover',
            (event: google.maps.Data.MouseEvent) => {
                this._mapInfoWindowSettings(map, event, infoWindow);
            },
        );
        map?.data.addListener('mouseout', () => {
            infoWindow.close();
        });
    }

    private _mapInfoWindowSettings(
        map: google.maps.Map,
        event: google.maps.Data.MouseEvent,
        infoWindow: google.maps.InfoWindow,
    ) {
        const toolTipAnchor = this._getToolTipCenter(
            event.feature.getGeometry(),
        );
        const tooltip: IDatiqMapTooltip = event.feature.getProperty('tooltip');
        const infowindowOptions: google.maps.InfoWindowOpenOptions =
            this._getInfoWindowOptions(event.latLng as any, map);
        const infoWindowContent: string = this._getInfoWindowContent(tooltip);
        infoWindow.setContent(infoWindowContent);
        infoWindow.setPosition(toolTipAnchor);
        infoWindow.open(infowindowOptions);
    }

    private _getInfoWindowContent(tooltip: IDatiqMapTooltip): string {
        return `<div class="flex flex-col text-center items-center text-xs !m-0 !p-0"> 
                <div>${tooltip.title}</div>
                <div class="font-bold">${tooltip.subtitle}</div>
              </div>`;
    }

    private _getInfoWindowOptions(
        latLng,
        map: google.maps.Map,
    ): google.maps.InfoWindowOpenOptions {
        return {
            anchor: latLng as google.maps.marker.AdvancedMarkerElement,
            map,
            shouldFocus: false,
        } as google.maps.InfoWindowOpenOptions;
    }

    private _getInfoWindow(): google.maps.InfoWindow {
        if (isNil(this._infoWindow)) {
            this._infoWindow = new google.maps.InfoWindow({
                disableAutoPan: true,
                maxWidth: 150,
            });
        }
        return this._infoWindow;
    }

    // #endregion

    // #region Mci Data Mapping Methods
    private _mapMciDataToFeature(
        mciData: MciRatio[],
        feature: IDatiqMapGeoJsonFeature,
    ): IDatiqMapGeoJsonFeature {
        const featureMciData: MciRatio = this._findMciDataForFeature(
            feature,
            mciData,
        );
        const style: IIqMcMapGeoJsonFeaturePropertiesStyles =
            this._mapMciDataToStyle(featureMciData, feature);
        const tooltip: IDatiqMapTooltip = this._generateToolTip(
            featureMciData,
            feature,
        );

        return {
            ...feature,
            properties: { ...feature.properties, style, tooltip },
        };
    }

    private _findMciDataForFeature(
        feature: IDatiqMapGeoJsonFeature,
        mciData: MciRatio[],
    ): MciRatio {
        return mciData.find(
            (mciSingleEntity: MciRatio) =>
                mciSingleEntity.geoCode === feature.properties.code,
        );
    }

    private _mapMciDataToStyle(
        mciData: MciRatio,
        feature: IDatiqMapGeoJsonFeature,
    ): IIqMcMapGeoJsonFeaturePropertiesStyles {
        return {
            ...feature?.properties?.style,
            fillColor: mciData?.capacityColor ?? defaultDatiqMapStyleConfiguration.fillColor,
        };
    }
    //# endregion

    // #region Tooltip Methods
    private _getToolTipCenter(geometry: google.maps.Data.Geometry) {
        const bounds = new google.maps.LatLngBounds();
        geometry.forEachLatLng((latLng) => {
            bounds.extend(latLng);
        });

        return this._getCenterFromBounds(bounds);
    }

    private _getCenterFromBounds(bounds: google.maps.LatLngBounds) {
        return new google.maps.LatLng({
            lat: bounds.getNorthEast().lat(),
            lng: bounds.getCenter().lng(),
        });
    }

    private _generateToolTip(
        featureMciData: MciRatio,
        feature: IDatiqMapGeoJsonFeature,
    ): IDatiqMapTooltip {
        if (!featureMciData) {
            return emptyDatIqMapTooltip;
        }
        const capacity: string = featureMciData?.capacity;
        const subtitle = !!capacity
            ? featureMciData.mci > 0
                ? `+${featureMciData.mci} ${capacity}`
                : `${featureMciData.mci} ${capacity}`
            : `Not Available`;
        return {
            title: feature?.properties?.name,
            subtitle,
            show: true,
        };
    }
    // #endregion
}
